import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { getAllOrders, getFilteredOrders, getOrderSearchTerm, getOrdersFilter } from '../../selectors/order'
import { getActiveAttendantId } from '../../selectors/attendant'
import { getIsKiosk, getTabsModeEnabled } from '../../selectors/menus'
import { ORDERS_STATUS_OPTIONS, STADIUM_ORDER_STATES } from '../../utils/orderStates'
import { createOrderParams, sortedOrders } from '../../utils/orderUtils'
import { getCurrentMenuId } from '../../selectors/appState'

export const openStates = [
  STADIUM_ORDER_STATES.TABBED,
  STADIUM_ORDER_STATES.TABBED_OFFLINE,
  STADIUM_ORDER_STATES.AUTHORIZATION_FAILED,
]

/**
 * Custom hook to manage the list of orders
 *
 * @returns {Object} - An object containing the getCurrentOrders function
 */
const useOrdersList = () => {
  const ordersFilter = useSelector(getOrdersFilter) || {}
  const activeAttendantId = useSelector(getActiveAttendantId)
  const isKiosk = useSelector(getIsKiosk)
  const tabsModeEnabled = useSelector(getTabsModeEnabled)
  const currentMenuUuid = useSelector(getCurrentMenuId)
  const byId = useSelector(getAllOrders)
  const search = useSelector(getOrderSearchTerm)

  const getOrders = useCallback(
    (isRefundReceiptsEnabled) => {
      let orders = []
      orders = Object.values(byId)

      const excludedStates = [
        STADIUM_ORDER_STATES.SUBMISSION_FAILED,
        STADIUM_ORDER_STATES.SUBMITTED,
        STADIUM_ORDER_STATES.AUTHORIZATION_FAILED,
      ]

      // Add refunded or cancelled orders to the list of orders to display.
      if (!isRefundReceiptsEnabled) {
        excludedStates.push(STADIUM_ORDER_STATES.CANCELLED)
      }

      // Filter orders by state.
      orders = orders?.filter((order) => {
        const curState = createOrderParams(order)?.state || order.state

        if (tabsModeEnabled) {
          if (currentMenuUuid !== order?.standMenuUuid) {
            return false
          }

          if (curState === STADIUM_ORDER_STATES.AUTHORIZATION_FAILED && !order?.refundable) {
            return true
          }
        }

        return !excludedStates.includes(curState)
      })

      if (search) {
        orders = getFilteredOrders(orders, search)
      }

      const nonRefundable = []
      const refundable = []

      sortedOrders(orders)?.forEach((order) => {
        const collection = order?.refundable ? refundable : nonRefundable
        collection.push(order)
      })

      return [...nonRefundable, ...refundable]
    },
    [byId, currentMenuUuid, search, tabsModeEnabled],
  )

  /**
   * Returns the current list of orders based on the orders filters and active attendant
   * This replaces the previous implementation in selectors/order that caused unnecessary re-renders
   *
   * @param {boolean} isRefundReceiptsEnabled - Flag indicating if refund receipts are enabled.
   * @returns {Array} - Filtered list of orders.
   */
  const getCurrentOrders = useCallback(
    (isRefundReceiptsEnabled) => {
      const orders = getOrders(isRefundReceiptsEnabled)

      switch (ordersFilter.showOrders) {
        case ORDERS_STATUS_OPTIONS.OPEN_ORDERS:
          return orders?.filter((order) => {
            if (order.employeeId !== activeAttendantId && createOrderParams(order)?.employeeId !== activeAttendantId) {
              return false
            }
            if (order.balanceDueInCents > 0 || (openStates.includes(order.state) && order.orderState !== 'closed')) {
              return true
            }
            return false
          })
        case ORDERS_STATUS_OPTIONS.CLOSED_ORDERS:
          return orders?.filter((order) => {
            return (
              (order.employeeId === activeAttendantId || createOrderParams(order)?.employeeId === activeAttendantId || isKiosk) &&
              order.balanceDueInCents <= 0 &&
              (!(order.state === STADIUM_ORDER_STATES.TABBED || order.state === STADIUM_ORDER_STATES.TABBED_OFFLINE) ||
                order.orderState === 'closed') &&
              order.orderNumber
            )
          })
        default:
          return orders
      }
    },
    [activeAttendantId, getOrders, isKiosk, ordersFilter.showOrders],
  )

  return {
    getCurrentOrders,
  }
}

export default useOrdersList
